import React, { forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';

import Button from '@dashboard/components/Button';

const VariableItem = styled(Button)`
  text-align: left;
  justify-content: flex-start;
  border-radius: 0;
  border: 0;
  :focus,
  :active {
    background: var(--gray-light);
    outline: none;
    ring: 0;
  }
  :hover {
    background: var(--gray-lighter);
  }
`;

const DialogContainer = styled.div`
  position: absolute;
  z-index: 1000;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 200px;
  max-height: 200px;
  overflow-y: auto;
`;

const VariableDialog = forwardRef(
  (
    {
      variables,
      onClickAdd,
      isOpen,
      onClose,
      position,
      selectedIndex,
      setSelectedIndex,
      firstItemRef,
    },
    ref,
  ) => {
    const itemRefs = useRef([]);

    useEffect(() => {
      if (isOpen) {
        itemRefs.current[selectedIndex]?.focus();
        itemRefs.current[selectedIndex]?.scrollIntoView({ block: 'nearest' });
      }
    }, [isOpen, selectedIndex]);

    const handleKeyDown = event => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setSelectedIndex(prevIndex =>
            prevIndex < variables.length - 1 ? prevIndex + 1 : prevIndex,
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setSelectedIndex(prevIndex => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
          break;
        case 'Enter':
          event.preventDefault();
          onClickAdd(variables[selectedIndex]);
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    if (!isOpen) return null;

    return (
      <DialogContainer
        ref={ref}
        style={{ top: `${position.top}px`, left: `${position.left}px` }}
        onKeyDown={handleKeyDown}
      >
        {variables.map((variable, index) => (
          <VariableItem
            className={`transition-colors w-full !ring-0 !focus:ring-0 ${
              index === selectedIndex ? 'bg-gray-light' : ''
            }`}
            key={`${variable.name}${index}`}
            onClick={() => {
              onClickAdd(variable);
              onClose();
            }}
            type="button"
            ref={el => {
              itemRefs.current[index] = el;
              if (index === 0) firstItemRef.current = el;
            }}
            tabIndex={index === selectedIndex ? 0 : -1}
            onFocus={() => setSelectedIndex(index)}
          >
            {variable.name}
          </VariableItem>
        ))}
      </DialogContainer>
    );
  },
);

VariableDialog.displayName = 'VariableDialog';

export default VariableDialog;
